import ClipLoader from "react-spinners/ClipLoader";
import type { FormData } from "types/index";
import WAurl from "utils/WAurl";
import { EmojiPicker } from "./EmojiPicker";
import { isPhnNoWithCountryCode } from "@utils/common";
import IntlTelInput, { CountryData } from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
import { useState } from "react";
import { verifyPhoneNumber } from "@utils/form";
interface MessageFormProps {
    onGenerateLink: (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    handleFormInputChange: (userInput: Partial<FormData>) => void;
    data: FormData;
    isLoading: boolean;
    validateForm: () => boolean;
}

export const MessageForm: React.FC<MessageFormProps> = (props) => {
    const {
        onGenerateLink,
        handleFormInputChange,
        validateForm,
        data: { isValidForm, country_code, phn_no, message },
        isLoading,
    } = props;
    const [WAlink, setWALink] = useState("");
    const [emojiOpen, setEmojiOpen] = useState(false);
    const handlePreviewClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (validateForm()) {
            const phoneNumber = isPhnNoWithCountryCode(phn_no) ? phn_no : country_code + phn_no;
            const whatsappURL = WAurl(phoneNumber, message);
            setWALink(whatsappURL);
        } else {
            e.preventDefault();
        }
    };
    const onEmojiClose = () => {
        handleFormInputChange({ isEmojiPopupOpen: false });
        setEmojiOpen(false);
    };

    return (
        <div className="flex min-w-xs md:min-w-md h-full z-0 lg:max-w-md border border-solid border-steel-light rounded-xl" style={{ flex: "1 0 0" }}>
            <div className="flex flex-col rounded-2xl p-3.5 min-h-full h-full w-full bg-white">
                <form onSubmit={onGenerateLink}>
                    <label className="font-semibold mt-2 mr-2 mb-0 ml-0 leading-10 text-sm">Enter your WhatsApp number</label>
                    <div className="block mb-5">
                        <IntlTelInput
                            containerClassName="intl-tel-input w-full leading-10"
                            inputClassName="bg-gray-light w-full rounded-xl h-12 focus:outline-none focus:ring-2 focus:ring-crayola-blue focus:border-transparent"
                            onSelectFlag={(e: string, countryData: CountryData, fullNumber: string, isValid: boolean) => {
                                handleFormInputChange({
                                    country_code: countryData.dialCode,
                                    isValidPhnNo: isValid,
                                    //  'phn_no':'+' + countryData.dialCode
                                });
                            }}
                            onPhoneNumberChange={(isValid: boolean, value: string) => {
                                if (verifyPhoneNumber(value)) {
                                    handleFormInputChange({ phn_no: value, isValidPhnNo: isValid });
                                }
                            }}
                            placeholder="Your phone number"
                            defaultCountry="in"
                            value={phn_no}
                            data-cy="wa-link-phnno"
                        />
                        <div className={`block text-xs mx-0 my-2 ${isValidForm ? "text-gray-dark" : "text-red-600"}`}>
                            {isValidForm ? "Note: Check if the country code you have entered is correct." : "Check your phone number (maybe it's wrong) and try again."}
                        </div>
                    </div>
                    <div>
                        <label className="font-semibold text-sm mt-2 mr-2 mb-0 ml-0 leading-5">Add your custom message</label>
                        <div className="relative mt-2.5 hidden lg:block md:block">
                            <button
                                className="p-0.5 rounded-full"
                                style={{ boxShadow: "rgb(0 0 0 / 30%) 0px 1px 4px" }}
                                id="emoji-open-button-shorturl"
                                type="button"
                                onClick={() => {
                                    handleFormInputChange({ isEmojiPopupOpen: !emojiOpen });
                                    setEmojiOpen(!emojiOpen);
                                }}
                            >
                                <span>😀</span>
                            </button>
                            <span className="ml-2.5 text-xs font-medium text-gray-600">Add emoji</span>
                            <div id="emoji-picker-shorturl">
                                <EmojiPicker open={emojiOpen} onClose={onEmojiClose} onEmojiSelect={(emoji) => handleFormInputChange({ message: message + emoji.native })} />
                            </div>
                        </div>

                        <div className="mt-2 mb-2.5">
                            <textarea
                                name="message"
                                placeholder="Add custom message that users will send to you"
                                rows={3}
                                className="bg-gray-light rounded-xl w-full border-slate-300 p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-crayola-blue focus:border-transparent"
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                    handleFormInputChange({ message: e.target.value });
                                }}
                                value={message}
                                data-cy="wa-link-message"
                            />
                            <div className="block text-xs my-2 text-gray-dark">Example: “Hello, I am interested to know more about your business”.</div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-2 justify-center items-center">
                        <a
                            href={WAlink}
                            className="wa-shorturl-preview hover:bg-crayola-blue mt-3"
                            // className="hover:bg-crayola-blue hover:text-white hover:no-underline bg-white border-gray-300 w-full rounded-full font-semibold p-4  text-center"
                            style={{
                                border: "1px solid #C8CEDB",
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handlePreviewClick}
                        >
                            Preview
                        </a>
                        <p>- or -</p>
                    </div>
                    <div className="flex gap-y-2 justify-center items-center mt-2">
                        <button
                            data-cy="generate-mylink"
                            className={`gb_link_generator_btn hover:bg-green-medium-dark self-center ${
                                isLoading ? "bg-green-medium-dark" : "bg-green-dark"
                            } border-slate-300 w-full rounded-full font-semibold p-4 text-white
                        focus:outline-none focus:ring-2 focus:ring-green-border focus:border-transparent`}
                            onClick={onGenerateLink}
                            type="submit"
                        >
                            {isLoading ? <ClipLoader color={"#ffffff"} loading={true} size={20} /> : "Generate my link"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
